/**
 * MusicPlayer 调试与常量（抽离自 MusicPlayer.js）
 *
 * 播放器行为常量一览（便于调参与文档化）：
 * - LYRIC_SYNC_THROTTLE_MS (120)：歌词同步节流
 * - PLAY_HISTORY_INTERVAL_MS (30000)：播放历史上报间隔
 * - AUTO_HIDE_DELAY_MS (10000)：停止后自动隐藏延迟
 * - AUDIO_LOAD_TIMEOUT_MS (10000)：音频 loadedmetadata 超时
 * - VIDEO_LOAD_TIMEOUT_MS (10000)：视频 loadedmetadata 超时
 * - SEEK_PREVIEW_RECT_CACHE_MS (100)：进度条 rect 缓存
 * - SEEK_PREVIEW_HIDE_DELAY_MS (180)：seek 气泡隐藏后停 rAF 延迟
 * - PANEL_SWIPE_DELAY_MS (180)：播放列表滑动手势确认移除延迟
 * - BASS_SHELF_FREQ_HZ (120)：TUAC 低架滤波器频率
 * - HIGH_SHELF_FREQ_48K (12000) / HIGH_SHELF_FREQ_DEFAULT (10000)：FidelityEnhancer 高频架滤波器
 */
import { detectWasmSimdSupport } from '../../utils/wasmSimd';

export const MP_DEBUG = process.env.NODE_ENV === 'development';
export const mpLog = (...args) => { if (MP_DEBUG) console.log(...args); };
export const mpWarn = (...args) => { if (MP_DEBUG) console.warn(...args); };

/**
 * 将客户端调试信息上报到服务端，在服务器终端显示（便于排查三星等设备 TUAC 问题）
 * @param {string} tag - 标签，如 'TUAC'
 * @param {string} message - 主信息
 * @param {object|Error|string} [payload] - 可选附加数据，Error 会转为 { message, name, stack }
 */
export function sendClientDebugToServer(tag, message, payload) {
  try {
    let pl = payload;
    if (payload instanceof Error) {
      pl = { name: payload.name, message: payload.message, stack: (payload.stack || '').slice(0, 500) };
    }
    fetch('/api/debug/client-log', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        tag: String(tag),
        message: String(message),
        payload: pl,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : ''
      })
    }).catch(() => {});
  } catch (_) {}
}

export const getSimdInfo = async (audioContext) => {
  let wasmSimd = false;
  try {
    wasmSimd = await detectWasmSimdSupport();
  } catch (_) {
    wasmSimd = false;
  }
  return {
    'WebAssembly SIMD': wasmSimd ? '支持' : '不支持',
    'AudioContext sampleRate': audioContext?.sampleRate ?? '—',
    'AudioContext state': audioContext?.state ?? '—'
  };
};

export const logSimdOnPlay = (audioContext) => {
  if (process.env.NODE_ENV !== 'development') return;
  getSimdInfo(audioContext).then((info) => {
    console.log('[MusicPlayer] SIMD 指令集使用情况', info);
  }).catch(() => {});
};

export const FADE_DURATION_MS = 3000;
export const FADE_TICK_MS = 50;
export const getFadeStep = (from, to) => Math.abs(to - from) * (FADE_TICK_MS / FADE_DURATION_MS);

// 播放器行为常量（便于调参与文档化）
export const LYRIC_SYNC_THROTTLE_MS = 120;       // 歌词同步节流
export const PLAY_HISTORY_INTERVAL_MS = 30000;   // 播放历史上报间隔（30 秒）
export const AUTO_HIDE_DELAY_MS = 10_000;       // 停止后自动隐藏延迟（10 秒）
export const AUDIO_LOAD_TIMEOUT_MS = 10000;      // tryLoadAudioFile 超时
export const VIDEO_LOAD_TIMEOUT_MS = 10000;      // 视频 loadedmetadata 超时
export const SEEK_PREVIEW_RECT_CACHE_MS = 100;   // 进度条 rect 缓存
export const SEEK_PREVIEW_HIDE_DELAY_MS = 180;   // seek 气泡隐藏后停 rAF 延迟
export const PANEL_SWIPE_DELAY_MS = 180;         // 播放列表滑动手势确认移除延迟
export const BASS_SHELF_FREQ_HZ = 120;           // TUAC 低架滤波器频率 (Hz)
export const HIGH_SHELF_FREQ_48K = 12000;        // FidelityEnhancer 高频架 48kHz 采样率
export const HIGH_SHELF_FREQ_DEFAULT = 10000;    // FidelityEnhancer 高频架默认

// 随机播放：最近 N 首去重窗口（避免“连着随机到同一首/刚播过的几首”）
// 说明：
// - N 会在运行时根据 playlist.length 自动 clamp 到 [0, playlist.length - 1]
// - 若 N 太大导致无候选，会自动降级放宽约束，确保一定能选出下一首
export const RANDOM_RECENT_DEDUP_N = 5;

// 避免在源码中直接出现旧地区码字面量（同时规避 eslint no-useless-concat）
export const ZH_CN = 'zh\u002dCN';
export const ZH_TW = 'zh\u002dTW';
export const ZH_CN_LC = 'zh\u002dcn';
export const ZH_TW_LC = 'zh\u002dtw';
